import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, ExternalLink, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavbarPermissions } from "@/hooks/useNavbarPermissions";
import { useAuth } from "@/hooks/useAuth";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { REPORTING_FLAG_KEY } from "@/pages/investment-reporting/ReportingCycleLayout";
import NotificationBell from "@/components/NotificationBell";

export const PartnerNavbar = () => {
  const { user, staffMember } = useAuth();
  const userRole = staffMember?.role_level || null;
  const team = staffMember?.team || null;

  const { canAccess } = useNavbarPermissions(userRole, team);
  // Investment team members manage podcast publishing, the Adviser Assist editor and client docs,
  // so they get the Admin dropdown (with just those entries) even without an admin role_level.
  const isInvestmentTeam = team === 'Investment';
  // Effective route gates, mirrored here so the navbar never advertises a page the router denies.
  // <ProtectedRoute requiredRoles={['admin','superadmin']}> ⇒ isAdminRole
  // <ProtectedRoute requiredTeam="Investment">              ⇒ canInvestmentRoutes (admins pass too)
  const isAdminRole = userRole === 'admin' || userRole === 'superadmin';
  const canInvestmentRoutes = isInvestmentTeam || isAdminRole;
  // Reader-shell access is by research capability, not team — so analyst-only advisers
  // (e.g. Bongiwe) who are not on the Investment team still get a way in.
  const { data: canReadResearch } = useResearchCapability("read");
  // Dark launch: the Investment Reporting entry point stays hidden until the feature_flags
  // row is seeded AND enabled — same flag the island itself gates on.
  const { enabled: reportingFlagOn } = useFeatureFlag(REPORTING_FLAG_KEY);

  if (!staffMember) return null;

  const profilePicture = user?.user_metadata?.avatar_url || 
                        user?.user_metadata?.picture;

  const fundManagerForms = [
    {
      name: "Meeting Request Form",
      url: "/meeting-request",
      external: false
    },
    {
      name: "Meeting Booked Conversion",
      url: "/meeting-booked",
      external: false
    },
    {
      name: "Meeting Report Writer",
      url: "/meeting-report",
      external: false
    }
  ];

  return (
    <div className="w-full bg-gradient-to-r from-primary via-primary to-accent border-b shadow-lg">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center min-h-16 gap-4 py-2 flex-wrap">
          <div className="flex items-center gap-6 flex-shrink-0">
            <div className="flex items-center gap-4">
              <Avatar className="h-10 w-10 border-2 border-primary-foreground/20">
                <AvatarImage 
                  src={profilePicture} 
                  alt={`${staffMember.first_name} ${staffMember.last_name}`}
                />
                <AvatarFallback className="bg-primary-foreground/10 text-primary-foreground">
                  <User className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-primary-foreground text-lg font-semibold">
                  {staffMember.first_name} {staffMember.last_name}
                </span>
                <span className="text-primary-foreground/80 text-sm">
                  {staffMember.role} • Partner Portal
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-1 items-center justify-center gap-3 flex-wrap">
            {canAccess('dashboard') && (
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary" 
                asChild
              >
                <Link to="/partner/dashboard">Dashboard</Link>
              </Button>
            )}

            {canAccess('blog_management') && isAdminRole && (
              <Button 
                variant="outline" 
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary" 
                asChild
              >
                <Link to="/partner/blog-management">Blog Management</Link>
              </Button>
            )}

            {canAccess('insights') && (
              <Button 
                variant="outline" 
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary" 
                asChild
              >
                <Link to="/insights">Insights</Link>
              </Button>
            )}
            
            {/* Markets. Investment Team only, by Sam's ruling on 2026-09-04, which REVERSES the
                staff-wide decision of 2026-08-27. The gate is canInvestmentRoutes precisely so it
                mirrors the route's requiredTeam="Investment" (both admit admin and superadmin).
                Keep the pair in step: gating only this link would hide the page while leaving
                /partner/markets reachable by URL to any signed-in staff member. */}
            {canInvestmentRoutes && (
              <Button
                variant="outline"
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/partner/markets">Markets</Link>
              </Button>
            )}

            {canAccess('external_meetings') && canInvestmentRoutes && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    CBAM Meetings
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="text-popover-foreground hover:text-accent-foreground">
                      Fund Managers
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-80 bg-popover border-border">
                      {/* All three meeting forms render off the SAME parent gate. The Request Form
                          previously carried an extra canAccess('meeting_request') check, and that
                          permission was disabled for Investment — the one team that runs fund
                          meetings — so they saw Booked and Report but not the Request Form that
                          starts the workflow, even though /meeting-request admits them. Three items
                          in one menu, all Investment-gated at the route, must gate identically. */}
                      {fundManagerForms.map((form) => (
                        <DropdownMenuItem key={form.url} asChild className="hover:bg-accent hover:text-accent-foreground">
                          {form.external ? (
                            <a
                              href={form.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between w-full text-popover-foreground hover:text-accent-foreground"
                            >
                              <span className="text-sm">{form.name}</span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <Link
                              to={form.url}
                              className="flex items-center justify-between w-full text-popover-foreground hover:text-accent-foreground"
                            >
                              <span className="text-sm">{form.name}</span>
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </DropdownMenuItem>
                      ))}
                      {(staffMember?.team === 'Investment' || staffMember?.role_level === 'superadmin') && (
                        <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                          <Link
                            to="/meetings/reference-data"
                            className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                          >
                            <span className="text-sm">Meetings Reference Data</span>
                          </Link>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  {/* CBAM MM Reporting used to be listed here as well as under Client Meetings.
                      It now appears under Client Meetings only. Nobody lost their route: every team
                      with external_meetings also has client_meetings (measured 2026-09-04 —
                      Admin and Investment hold both; Advisory and Operations hold client_meetings
                      alone), and superadmins bypass canAccess entirely. */}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {canAccess('client_meetings') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    Client Meetings
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  {canAccess('internal_distribution') && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/partner/internal-distribution"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Internal Distribution
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canAccess('document_library') && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/partner/document-library"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Document Library
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link 
                      to="/partner/adviser-client-presentation"
                      className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                    >
                      Adviser-Client Presentation
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link
                      to="/partner/cbam-mm-reporting"
                      className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                    >
                      CBAM MM Reporting
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {canAccess('policy_centre') && canAccess('tier_1_policy') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    Policy Centre
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  {canAccess('tier_1_policy') && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/partner/policy-centre/tier-1"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Tier 1 Group Investment Policy
                      </Link>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Portfolio Management - matches the routes' requiredTeam="Investment" (admins pass) */}
            {canInvestmentRoutes && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    Portfolio Management
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/portfoliomanagement/p" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      P Portfolio Management
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/portfoliomanagement/r" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      R Portfolio Management
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/portfoliomanagement/dist" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      Dist Portfolio Management
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/portfoliomanagement/mm" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      MM Portfolio Management
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Benchmark Maintenance - Investment team + Admin/SuperAdmin */}
            {(staffMember?.team === 'Investment' || staffMember?.role_level === 'admin' || staffMember?.role_level === 'superadmin') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    Benchmark Maintenance
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/benchmark-maintenance/fund-house" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      BM Fund House Updating
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Investment Reporting (review + sign-off shell) - Investment ∪ Operations ∪ Admin/SuperAdmin,
                congruent with the reporting RLS. Deliberately a STATIC team/role check (same shape as
                Benchmark Maintenance above) rather than a can_review_reporting() query: the navbar renders
                on every partner page, and the authoritative gate already runs server-side inside
                InvestmentReportingLayout via useReportingAccess().
                ALSO gated on the `investment_reporting_enabled` feature flag (dark launch) —
                the flag is required IN ADDITION to the team/role test, so the link stays hidden
                until the reporting DDL is applied and Sam enables the flag. */}
            {reportingFlagOn && (staffMember?.team === 'Investment' || staffMember?.team === 'Operations' || staffMember?.role_level === 'admin' || staffMember?.role_level === 'superadmin') && (
              <Button
                variant="outline"
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/partner/investment-reporting">Investment Reporting</Link>
              </Button>
            )}

            {/* Research Hub (reader shell) - anyone with research read access, incl. analyst-only advisers */}
            {canReadResearch && (
              <Button
                variant="outline"
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/partner/research-hub">Research Hub</Link>
              </Button>
            )}

            {/* Research ops console - Investment team + Admin/SuperAdmin */}
            {(staffMember?.team === 'Investment' || staffMember?.role_level === 'admin' || staffMember?.role_level === 'superadmin') && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    Research (ops)
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/house-view" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      House View Feed
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/research" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      Research Notes
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                    <Link to="/partner/research/new" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                      New Research Note
                    </Link>
                  </DropdownMenuItem>
                  {(staffMember?.role_level === 'admin' || staffMember?.role_level === 'superadmin') && (
                    <>
                      <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                        <Link to="/partner/research/manager" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                          Manage Research
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                        <Link to="/partner/research/taxonomy" className="flex items-center w-full text-popover-foreground hover:text-accent-foreground">
                          Taxonomy
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {canAccess('profile_settings') && (
              <Button 
                variant="outline" 
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary" 
                asChild
              >
                <Link to="/partner/profile">Profile Settings</Link>
              </Button>
            )}

            {(userRole === 'admin' || userRole === 'superadmin') && (
              <Button
                variant="outline"
                size="sm"
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/partner/compliance">Compliance</Link>
              </Button>
            )}

            {(canAccess('admin') || isInvestmentTeam) && (isAdminRole || isInvestmentTeam) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  >
                    Admin
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border-border z-50">
                  {canAccess('admin_dashboard') && isAdminRole && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/admin/dashboard"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canAccess('portfolio_management') && isAdminRole && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/admin/portfolio-management"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Portfolio Management
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canAccess('security_dashboard') && isAdminRole && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/admin/security"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Security Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(canAccess('client_docs_manager') || isInvestmentTeam) && canInvestmentRoutes && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/admin/client-docs"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Client Docs Manager
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Route is requiredRoles={['superadmin']} (App.tsx). The DB permission had this
                      enabled for Investment and Operations 'user', so those staff saw the entry and
                      were bounced to the dashboard with an Access Denied toast. Advertising is now
                      narrowed to match enforcement — deliberately NOT widening the route, since that
                      would grant new access. If these editors SHOULD be admin-reachable, widen the
                      route instead and revert this line. */}
                  {userRole === 'superadmin' && canAccess('about_editor') && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link
                        to="/admin/about-editor"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        About Page Editor
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Same mismatch: the route is superadmin-only while this advertised to 'admin'
                      too, so admins got an Access Denied bounce. NB the page's own guard
                      (Tier1PolicyEditor.tsx) permits admin OR superadmin. RESOLVED: the owner
                      confirmed admins should have this, so the ROUTE was widened to
                      ['admin','superadmin'] and the advertising restored to match. */}
                  {(userRole === 'admin' || userRole === 'superadmin') && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link
                        to="/admin/tier1-policy-editor"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Tier 1 Policy Editor
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canAccess('team_management') && isAdminRole && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/admin/team-management"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Team Management
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Two fully-built admin consoles had no entry anywhere in the product and were
                      reachable only by typing the URL: /admin/advisers (create adviser firms, issue
                      invites — the front door to the whole adviser build target) and /admin/podcast
                      (the sole UI for the mp4 → storage → YouTube → RSS publish pipeline). Neither
                      is flag-gated; the nav entries were simply never added. Gated to match their
                      routes exactly: requiredRoles={['admin','superadmin']}. */}
                  {(userRole === 'admin' || userRole === 'superadmin') && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link
                        to="/admin/advisers"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Adviser Firms &amp; Invites
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canInvestmentRoutes && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link
                        to="/admin/podcast"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Podcast Publishing
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canInvestmentRoutes && (
                    <DropdownMenuItem asChild className="hover:bg-accent hover:text-accent-foreground">
                      <Link 
                        to="/partner/adviser-assist-editor"
                        className="flex items-center w-full text-popover-foreground hover:text-accent-foreground"
                      >
                        Twice-Monthly Adviser Assist Editor
                      </Link>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <NotificationBell />
          </div>
        </div>
      </div>
    </div>
  );
};